import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

/** Drag this far past `min` and the panel collapses instead of stopping dead. */
const COLLAPSE_SLOP = 64;

interface PanelResizeOptions {
  /** The element being resized. Its rendered width seeds each drag. */
  panelRef: RefObject<HTMLElement | null>;
  onWidth: (width: number) => void;
  min: number;
  /** Upper bound in px, or a lazy one measured at grab time. */
  max: number | (() => number);
  /** Which edge of the panel the handle sits on — `start` means dragging left widens it. */
  edge: 'start' | 'end';
  /** Supplied only where collapsing is meaningful; enables snap-collapse. */
  onCollapse?: () => void;
}

/**
 * Pointer-drag resizing for a flex panel.
 *
 * Two details here are load-bearing rather than stylistic:
 *
 * `setPointerCapture` — the session's desktop tab renders noVNC in an `<iframe>`,
 * and an iframe swallows the pointer stream the moment the cursor crosses into
 * it. Capture keeps every move and the final `pointerup` addressed to the handle,
 * so the drag survives passing over the desktop. The handlers therefore belong on
 * the handle element itself, never on `window`.
 *
 * The starting width comes from the DOM, not from React state. Panels here are
 * CSS-clamped (`max-width: calc(100% - …)`) so a narrow window can render one
 * *narrower* than its stored width; seeding from state would make the panel jump
 * to the stale value on grab.
 */
export function usePanelResize({ panelRef, onWidth, min, max, edge, onCollapse }: PanelResizeOptions) {
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const frame = useRef(0);

  const cancelFrame = useCallback(() => {
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    }
  }, []);

  useEffect(() => cancelFrame, [cancelFrame]);

  const endDrag = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      cancelFrame();
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      setDragging(false);
    },
    [cancelFrame],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      const panel = panelRef.current;
      if (!panel) return;
      startX.current = e.clientX;
      startWidth.current = panel.getBoundingClientRect().width;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    },
    [panelRef],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!dragging) return;
      const delta = edge === 'start' ? startX.current - e.clientX : e.clientX - startX.current;
      const raw = startWidth.current + delta;

      // Snap-collapse. Tear the drag down *before* handing off: the caller
      // unmounts the handle in response, which would otherwise strand us
      // mid-gesture with capture still held.
      if (onCollapse && raw < min - COLLAPSE_SLOP) {
        endDrag(e);
        onCollapse();
        return;
      }

      const upper = typeof max === 'function' ? max() : max;
      const next = Math.round(Math.min(Math.max(raw, min), Math.max(upper, min)));

      // Coalesce to one layout pass per frame — a raw pointermove stream would
      // otherwise re-fit the terminal and re-position the desktop several times
      // between paints.
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        onWidth(next);
      });
    },
    [dragging, edge, endDrag, max, min, onCollapse, onWidth],
  );

  return {
    dragging,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
